/**
 * The layer-spec rule MapLibre Native adds on top of the style-spec
 * (#336 MR2b): every property the style-spec types as an array (or as
 * `padding`, which it lets be a bare number) must be written as an array.
 * MapLibre Native Android crashes on a scalar in an array-typed property (a
 * trap met while porting the forecast map to mobile, !319), where GL JS
 * and the style-spec validator accept the value or fail softly.
 *
 * The list is conservative: it takes every array-like type in the
 * style-spec, though the crash was met on plain `array` properties only;
 * `padding`, `numberArray`, `colorArray` and
 * `variableAnchorOffsetCollection` are included **[unverified]** because
 * writing them as arrays is always valid.
 *
 * Pure and dependency-free so mobile can assert it in dev and in tests
 * (step 2 MR3's `nativeSafe`); the core test keeps the list in step with the
 * style-spec's own reference.
 */

/**
 * Layout and paint properties typed `array`, `padding`,
 * `variableAnchorOffsetCollection`, `numberArray` or `colorArray` in
 * `@maplibre/maplibre-gl-style-spec` 26.2.1 (`latest.json`).
 */
export const NATIVE_ARRAY_PROPERTIES: readonly string[] = Object.freeze([
  'circle-translate',
  'fill-extrusion-translate',
  'fill-translate',
  'hillshade-highlight-color',
  'hillshade-illumination-altitude',
  'hillshade-illumination-direction',
  'hillshade-shadow-color',
  'icon-offset',
  'icon-padding',
  'icon-text-fit-padding',
  'icon-translate',
  'line-dasharray',
  'line-translate',
  'text-font',
  'text-offset',
  'text-translate',
  'text-variable-anchor',
  'text-variable-anchor-offset',
  'text-writing-mode',
]);

const ARRAY_PROPERTIES = new Set(NATIVE_ARRAY_PROPERTIES);

type LayerLike = {
  id: string;
  layout?: Record<string, unknown>;
  paint?: Record<string, unknown>;
  filter?: unknown;
};

/**
 * What in `layer` MapLibre Native would choke on, one message per problem;
 * empty when the layer is safe. An expression is an array too, so a
 * data-driven value passes.
 */
export const nativeSpecViolations = (layer: LayerLike): string[] => {
  const violations: string[] = [];
  for (const group of ['layout', 'paint'] as const) {
    for (const [property, value] of Object.entries(layer[group] ?? {})) {
      if (ARRAY_PROPERTIES.has(property) && !Array.isArray(value)) {
        violations.push(
          `${layer.id}: ${group} '${property}' must be an array, got ${JSON.stringify(value)}`,
        );
      }
    }
  }
  if (layer.filter !== undefined && !Array.isArray(layer.filter)) {
    violations.push(`${layer.id}: 'filter' must be an expression array`);
  }
  return violations;
};
